import {ApplicationData, Options, Question, QuestionOptions} from '@agenium-ai/agenium-client-sdk/dist/models';
import {Section} from '@agenium-ai/agenium-client-sdk/dist/models/section';
import {Dialog, DialogContent, DialogTitle, IconButton, makeStyles} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {observer} from 'mobx-react-lite';
import React, {createRef, useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {firstValueFrom} from 'rxjs';
import {useAgenium} from '../../Core/agenium/ageniumContext';
import {useAgeniumUser} from '../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../Core/mobx/RootStore';
import LoadingSpinner from '../../UI/LoadingSpinner';
import Address from './InstantAppModalPages/Address';
import AgeniumApplication from './InstantAppModalPages/AgeniumApplication/AgeniumApplication';
import {AllDone} from './InstantAppModalPages/AllDone';
import ApprovalInformation from './InstantAppModalPages/ApprovalInformation';
import BankingInformation from './InstantAppModalPages/BankingInformation';
import Beneficiaries from './InstantAppModalPages/Beneficiaries';
import Birthplace from './InstantAppModalPages/Birthplace';
import BookAppointment from './InstantAppModalPages/BookAppointment';
import ConditionalCoverage from './InstantAppModalPages/ConditionalCoverage';
import ConditionalCoverageConfirm from './InstantAppModalPages/ConditionalCoverageConfirm';
import DecisionApproval from './InstantAppModalPages/DecisionApproval';
import DecisionDeclined from './InstantAppModalPages/DecisionDeclined';
import DecisionHardStop from './InstantAppModalPages/DecisionHardStop';
import DecisionManualUnderwriting from './InstantAppModalPages/DecisionManualUnderwriting';
import Employer from './InstantAppModalPages/Employer';
import ExistingLifeInsurance from './InstantAppModalPages/ExistingLifeInsurance';
import LastDoctorVisit from './InstantAppModalPages/LastDoctorVisit';
import Review from './InstantAppModalPages/ReviewScreen';
import {SendDocusign} from './InstantAppModalPages/SendDocusign';
import {StartApplication} from './InstantAppModalPages/StartApplication';
import UnderwritingDisclosure from './InstantAppModalPages/UnderwritingDisclosure';
import {VerifyIdentity} from './InstantAppModalPages/VerifyIdentity';
import {getApprovalQuoteFromCalcfocus} from './InstantAppModalPages/utils';
import {updateHubspotProsperDealDocusignComplete} from './deal';

export type InstantAppModalPage =
    | 'START_APPLICATION'
    | 'APPLICATION'
    | 'REVIEW'
    // | 'GENERAL_QUESTIONS'
    | 'VERIFY_IDENTITY'
    | 'BOOK_APPOINTMENT'
    | 'BANKING_INFO'
    | 'BANKING_INFO_LF'
    | 'APPROVAL_INFO'
    | 'APPROVAL_INFO_LF'
    | 'DECISION_APPROVAL'
    | 'DECISION_DECLINED'
    | 'DECISION_MANUAL_UNDERWRITING'
    | 'DECISION_HARD_STOP'
    | 'SEND_DOCUSIGN'
    | 'ALL_DONE'
    | 'BENEFICIARIES'
    | 'EMPLOYER'
    | 'EXISTING_LIFE_INSURANCE'
    | 'LAST_DOCTOR_VISIT'
    | 'BIRTHPLACE'
    | 'ADDRESS'
    | 'CONDITIONAL_COVERAGE'
    | 'CONDITIONAL_COVERAGE_CONFIRM'
    | 'UNDERWRITING_DISCLOSURE_POST';

interface FullScreenModalProps {
    onClose: () => void;
    parameterStore: ParameterStore;
    formType: 'single-quote' | 'combination-quote' | undefined;
    quoteResponse: Outlist[];
}
const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            margin: 0,
            width: '100%',
            height: '100%',
        },
        '@media only screen and (min-width: 480px)': {
            '& .MuiDialogContent-root': {
                marginLeft: '80px',
            },
        },
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

export type Outlist = {
    name: string;
    value: string | number | number[];
    type: string;
    fieldName?: string;
};

const InstantAppModal: React.FC<FullScreenModalProps> = observer(
    ({onClose, parameterStore, formType, quoteResponse}) => {
        const ageniumContext = useAgenium();
        const [calcFocusDataLocal, setCalcFocusDataLocal] = useState<{
            faceAmount?: number;
            monthlyPremium?: number;
            productName?: string;
            req?: any;
            out?: Outlist[];
        }>({});
        const dialogContentRef = createRef();
        const location = useLocation();
        const history = useHistory();
        const searchParams = new URLSearchParams(location.search);
        const modalPage = searchParams.get('modalPage')?.toUpperCase() as InstantAppModalPage | null;
        const classes = useStyles();
        const {ageniumUser} = useAgeniumUser();
        const contactFormStore = RootStore.agentPortalStore.contactsFormStore;

        const [prevModal, setPrevModal] = useState<string | null | undefined>();
        function setModalPage(modalPage: InstantAppModalPage | null) {
            if (modalPage) {
                setPrevModal(searchParams.get('modalPage')?.toUpperCase() ?? null);
                searchParams.set('modalPage', modalPage);
                history.push({
                    search: searchParams.toString(),
                });
            } else {
                setPrevModal(null);
                onClose();
            }
        }

        const onError = (error: any) => {
            RootStore.uiStore.showSnackbar(error?.message, 'error');
        };

        useEffect(() => {
            if (
                (modalPage === 'DECISION_APPROVAL' ||
                    modalPage === 'BOOK_APPOINTMENT' ||
                    modalPage === 'BANKING_INFO_LF' ||
                    modalPage === 'BANKING_INFO') &&
                parameterStore.approval.decisionLater1HrMark.value
            ) {
                setModalPage('APPROVAL_INFO_LF');
            }
        }, []);

        function getApplicationContent() {
            if (ageniumContext.ageniumUserLoading) {
                return <LoadingSpinner />;
            }
            if (!ageniumContext.hasValidAgent) {
                return (
                    <div>
                        You are not appointed with EMC. Have your manager add your Agenium appointment info, and try
                        logging out and logging back in.
                    </div>
                );
            }
            return (
                <>
                    {modalPage === 'START_APPLICATION' && (
                        <StartApplication
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            formType={formType}
                            quoteResponse={quoteResponse}
                            onExit={() => setModalPage(null)}
                            onNext={() => setModalPage('REVIEW')}
                        />
                    )}
                    {modalPage === 'APPLICATION' && (
                        <AgeniumApplication
                            quoteResponse={quoteResponse}
                            dialogContentRef={dialogContentRef}
                            updateLocalCalcfocusData={setCalcFocusDataLocal}
                            ageniumContext={ageniumContext}
                            onBack={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'REVIEW' && (
                        <Review
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            formType={formType}
                            quoteResponse={quoteResponse}
                            onNext={() => setModalPage('BENEFICIARIES')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {/*{modalPage === 'GENERAL_QUESTIONS' && (
                        <GeneralQuestions
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('BENEFICIARIES')}
                            onExit={() => setModalPage(null)}
                        />
                    )}*/}
                    {modalPage === 'VERIFY_IDENTITY' && (
                        <VerifyIdentity
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            quoteResponse={quoteResponse}
                            onNext={() => setModalPage('APPLICATION')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'BENEFICIARIES' && (
                        <Beneficiaries
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('EMPLOYER')}
                            onBack={() => setModalPage('REVIEW')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'LAST_DOCTOR_VISIT' && (
                        <LastDoctorVisit
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onBack={() => setModalPage('EXISTING_LIFE_INSURANCE')}
                            onNext={() => setModalPage('BIRTHPLACE')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'ADDRESS' && (
                        <Address
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('VERIFY_IDENTITY')}
                            onBack={() => setModalPage('BIRTHPLACE')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'BIRTHPLACE' && (
                        <Birthplace
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('ADDRESS')}
                            onBack={() => setModalPage('LAST_DOCTOR_VISIT')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'EMPLOYER' && (
                        <Employer
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('EXISTING_LIFE_INSURANCE')}
                            onBack={() => setModalPage('BENEFICIARIES')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'EXISTING_LIFE_INSURANCE' && (
                        <ExistingLifeInsurance
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('LAST_DOCTOR_VISIT')}
                            onBack={() => setModalPage('EMPLOYER')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'DECISION_APPROVAL' && (
                        <DecisionApproval
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onShareDecisionNow={function (): void {
                                setModalPage('APPROVAL_INFO');
                            }}
                            onBookAppointment={function (): void {
                                setModalPage('BOOK_APPOINTMENT');
                            }}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'DECISION_DECLINED' && (
                        <DecisionDeclined
                            onExit={() => setModalPage(null)}
                            onNext={function (): void {
                                searchParams.delete('modalPage');
                                history.push({
                                    pathname: `/agent/form/${contactFormStore.email}/${contactFormStore.id}/1`,
                                });
                            }}
                            parameterStore={parameterStore}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'DECISION_MANUAL_UNDERWRITING' && (
                        <DecisionManualUnderwriting
                            onExit={() => setModalPage(null)}
                            onNext={function (): void {
                                setModalPage('CONDITIONAL_COVERAGE');
                            }}
                            onBackToClients={() => {
                                searchParams.delete('modalPage');
                                history.push({
                                    pathname: '/agent',
                                });
                            }}
                            contactFormStore={contactFormStore}
                            parameterStore={parameterStore}
                            prevModal={prevModal}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'DECISION_HARD_STOP' && (
                        <DecisionHardStop
                            onExit={() => setModalPage(null)}
                            onNext={function (): void {
                                history.push({
                                    pathname: '/agent',
                                });
                            }}
                            parameterStore={parameterStore}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'BOOK_APPOINTMENT' && (
                        <BookAppointment
                            onNext={() => setModalPage('APPROVAL_INFO')}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {(modalPage === 'BANKING_INFO' || modalPage === 'BANKING_INFO_LF') && (
                        <BankingInformation
                            parameterStore={parameterStore}
                            onNext={async () => {
                                // save form because some of this info
                                // is in the parameter store
                                await contactFormStore.saveForm();
                                setModalPage('UNDERWRITING_DISCLOSURE_POST');
                            }}
                            onBack={() => setModalPage('APPROVAL_INFO')}
                            onExit={() => setModalPage(null)}
                            calcFocusDataLocal={calcFocusDataLocal}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'UNDERWRITING_DISCLOSURE_POST' && (
                        <UnderwritingDisclosure
                            parameterStore={parameterStore}
                            onNext={() => setModalPage('SEND_DOCUSIGN')}
                            onBack={() => setModalPage('BANKING_INFO')}
                            onExit={() => setModalPage(null)}
                            ageniumContext={ageniumContext}
                            isPostDecision={true}
                        />
                    )}
                    {modalPage === 'APPROVAL_INFO' && (
                        <ApprovalInformation
                            dialogContentRef={dialogContentRef}
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            calcFocusDataLocal={calcFocusDataLocal}
                            setCalcFocusDataLocal={setCalcFocusDataLocal}
                            onNext={async () =>
                                new Promise<void>((resolve, _reject) => {
                                    resolve();
                                    setModalPage('BANKING_INFO');
                                })
                            }
                            onExit={() => setModalPage(null)}
                            // onBack={() => setModalPage('DECISION_APPROVAL')}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'APPROVAL_INFO_LF' && (
                        <ApprovalInformation
                            dialogContentRef={dialogContentRef}
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            calcFocusDataLocal={calcFocusDataLocal}
                            setCalcFocusDataLocal={setCalcFocusDataLocal}
                            {...(prevModal === 'BANKING_INFO_LF'
                                ? {
                                      onBack: () => setModalPage('BANKING_INFO_LF'),
                                  }
                                : {})}
                            onBackToClients={() => {
                                searchParams.delete('modalPage');
                                history.push({
                                    pathname: '/agent',
                                });
                            }}
                            onNext={async () =>
                                new Promise<void>((resolve, reject) => {
                                    try {
                                        if (
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Bank_RoutingNumber',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Bank_AccountNumber',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Bank_AccountType',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_LapseNotice',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentMode',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentModeEFTInitialDraft',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentFrequency',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentFrequencyOther',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_RequestedEffectiveDate',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_FaceAmountFinal',
                                            )?.value
                                        ) {
                                            ageniumContext?.sdk?.getSections().subscribe({
                                                next: async (section: Section[] | undefined) => {
                                                    let case_Applicant_FaceAmountFinal: Question | undefined =
                                                        undefined;
                                                    for (let t = 0; section && t < section.length; t++) {
                                                        for (
                                                            let i = 0;
                                                            section[t].questions && i < section[t].questions.length;
                                                            i++
                                                        ) {
                                                            if (
                                                                section[t].questions[i].key ===
                                                                'Case_Applicant_FaceAmountFinal'
                                                            )
                                                                case_Applicant_FaceAmountFinal =
                                                                    section[t].questions[i];
                                                            section[t].questions[i].questions?.forEach((l) => {
                                                                if (l.key === 'Case_Applicant_FaceAmountFinal')
                                                                    case_Applicant_FaceAmountFinal = l;
                                                            });
                                                        }
                                                    }
                                                    if (case_Applicant_FaceAmountFinal) {
                                                        if (
                                                            calcFocusDataLocal &&
                                                            calcFocusDataLocal.out &&
                                                            calcFocusDataLocal.out.length
                                                        )
                                                            case_Applicant_FaceAmountFinal.value =
                                                                calcFocusDataLocal.out.find(
                                                                    (t: Outlist) => t.fieldName === 'faceAmount',
                                                                )?.value;
                                                        else
                                                            case_Applicant_FaceAmountFinal.value = (
                                                                await getApprovalQuoteFromCalcfocus(
                                                                    ageniumContext.applicationData as ApplicationData[],
                                                                    ageniumUser,
                                                                )
                                                            )?.out?.find(
                                                                (t: Outlist) => t.fieldName === 'faceAmount',
                                                            )?.value;
                                                        try {
                                                            if (
                                                                ageniumContext.sdk &&
                                                                (await firstValueFrom(
                                                                    ageniumContext.sdk.submitAnswer(
                                                                        case_Applicant_FaceAmountFinal,
                                                                    ),
                                                                ))
                                                            ) {
                                                                let options: Options = {};
                                                                let questionOptions: QuestionOptions = {
                                                                    nextUnansweredQuestion: true,
                                                                };
                                                                options.questionOptions = questionOptions;
                                                                await firstValueFrom(
                                                                    ageniumContext.sdk.getNextQuestion(
                                                                        case_Applicant_FaceAmountFinal,
                                                                        options,
                                                                    ),
                                                                );
                                                                resolve();
                                                                setModalPage('SEND_DOCUSIGN');
                                                            }
                                                        } catch (e) {
                                                            onError(
                                                                'Error during submission of the Applicant Face Amount Final',
                                                            );
                                                            resolve();
                                                        }
                                                    }
                                                },
                                            });
                                        } else {
                                            resolve();
                                            setModalPage('BANKING_INFO');
                                        }
                                    } catch (e) {
                                        console.log(e);
                                        onError(e);
                                        resolve();
                                    }
                                })
                            }
                            onExit={() => setModalPage(null)}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'SEND_DOCUSIGN' && (
                        <SendDocusign
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onDocusignSignComplete={async () => {
                                await updateHubspotProsperDealDocusignComplete();
                                setModalPage('ALL_DONE');
                            }}
                            onExit={() => setModalPage(null)}
                            onBack={() => setModalPage('BANKING_INFO')}
                            ageniumContext={ageniumContext}
                            onDecisionRedirect={(decision) => {
                                if (decision === 'REFERRED') {
                                    setModalPage('DECISION_MANUAL_UNDERWRITING');
                                } else if (decision === 'DECLINED' || decision === 'CANCELLED') {
                                    setModalPage('DECISION_DECLINED');
                                }
                            }}
                        />
                    )}
                    {modalPage === 'CONDITIONAL_COVERAGE' && (
                        <ConditionalCoverage
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            ageniumContext={ageniumContext}
                            onNext={() => setModalPage('CONDITIONAL_COVERAGE_CONFIRM')}
                        />
                    )}
                    {modalPage === 'CONDITIONAL_COVERAGE_CONFIRM' && (
                        <ConditionalCoverageConfirm
                            parameterStore={parameterStore}
                            quoteResponse={quoteResponse}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'ALL_DONE' && (
                        <AllDone
                            calcFocusDataLocal={calcFocusDataLocal}
                            onBackToClientsClicked={() => setModalPage(null)}
                            ageniumContext={ageniumContext}
                        />
                    )}
                </>
            );
        }

        return (
            <Dialog onClose={() => setModalPage(null)} fullScreen className={classes.dialog} open>
                <DialogTitle>
                    <img src="/img/logo3.svg" style={{width: '100px'}} alt="Amplify Logo - Instant Application" />
                    <IconButton aria-label="close" className={classes.closeButton} onClick={() => setModalPage(null)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent ref={dialogContentRef}>{getApplicationContent()}</DialogContent>
            </Dialog>
        );
    },
);

InstantAppModal.displayName = 'InstantAppModal';

export default InstantAppModal;
